export enum PAYMENT_METHODS {
  "money" = '01',
  "check" = '02',

  /**
   * The PAYMENT in credit card should create the titles against the card admin.
   * 
   * The MekanicWEB sytem no accept RECEIVES in credit card.
   */
  "credit" = '03',

  /**
   * The PAYMENT in debit card should move bank balance.
   * 
   * The RECEIVES should create titles against the card admin.
   */
  "debit" = '04',
  "shopCard" = '05',
  "food" = '10',
  "fastFood" = '11',
  "gift" = '12',
  "fuel" = '13',
  "duplicate" = '14',
  "bankSlip" = '15',
  "deposit" = '16',
  "dynamicPix" = '17',
  "bankTransfer" = '18',
  "cashback" = '19',
  "staticPix" = '20',
  "shopCredit" = '21',
  "notInformed" = '22',
  "noPayment" = '90', // used for devolution
  "other" = '99',
  // "credit_with_tef" = '03-tef', // use only for lows that will generate tef integration
  // "debit_with_tef" = '04-tef', // use only for lows that will generate tef integration
}

export type PAYMENT_METHOD = keyof typeof PAYMENT_METHODS;

/**
 * Formas que o cadastro da operadora e a baixa oferecem além do crédito e do débito. Não são formas fiscais:
 * a baixa grava o código fiscal (`fiscal`) e informa o canal à API, que gera os títulos contra a operadora.
 *
 * `payment-link` é crédito (03) e `pix-card-admin` é PIX estático (20). Trocar o código fiscal de um canal é
 * alterar só este mapeamento. Mesma tabela da API (`receive-plan.helper.ts`).
 */
export const CARD_ADMIN_CHANNELS = {
  'payment-link': { channel: 'link', fiscal: PAYMENT_METHODS.credit },
  'pix-card-admin': { channel: 'pix', fiscal: PAYMENT_METHODS.staticPix },
} as const;

export type CardAdminChannelPayForm = keyof typeof CARD_ADMIN_CHANNELS;
export type CardAdminChannel = (typeof CARD_ADMIN_CHANNELS)[CardAdminChannelPayForm]['channel'];

export function isCardAdminChannelPayForm(value: unknown): value is CardAdminChannelPayForm {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(CARD_ADMIN_CHANNELS, value);
}

/** Valor do select de forma de pagamento: forma fiscal, variante de TEF ou canal da operadora. */
export type PaymentMethodSelection = PAYMENT_METHODS | TEF_PAYMENT_METHOD | CardAdminChannelPayForm;

/** Canal (`link`/`pix`) da forma escolhida, ou `undefined` para as formas comuns. */
export function cardAdminChannelOf(method: unknown): CardAdminChannel | undefined {
  return isCardAdminChannelPayForm(method) ? CARD_ADMIN_CHANNELS[method].channel : undefined;
}

/**
 * `payForm` da linha de taxa da operadora a procurar para a forma escolhida: o próprio canal, ou o código
 * fiscal puro (`03-tef` -> `03`).
 */
export function cardAdminRowPayForm(method: PaymentMethodSelection | null | undefined): string | undefined {
  if (isCardAdminChannelPayForm(method)) return method;
  return basePaymentMethod(method);
}

/** Formas de pagamento que existem só para a baixa disparar a integração via TEF. */
export type TEF_PAYMENT_METHOD = "03-tef" | "04-tef";

/**
 * Forma de pagamento sem o sufixo do TEF: `'03-tef'` → `'03'`, `'04-tef'` → `'04'`.
 *
 * `03-tef` e `04-tef` não são formas de pagamento de verdade — são o sinal de que a baixa precisa
 * abrir o dialog do TEF. Todo o resto do sistema conhece apenas o código de dois dígitos: a taxa
 * cadastrada na administradora (`persons.cardAdmin[].payForm`), a regra de parcela única do débito,
 * o próprio `PAYMENT_METHODS`. Comparar o valor cru com `PAYMENT_METHODS` numa baixa por TEF **nunca
 * casa**, e o sintoma não é erro: é campo que fica vazio em silêncio.
 *
 * Idempotente para forma comum, porque nenhum código do enum tem caractere não numérico.
 */
export function basePaymentMethod(
  method: PaymentMethodSelection | null | undefined
): PAYMENT_METHODS | undefined {
  if (!method) {
    return undefined;
  }

  if (isCardAdminChannelPayForm(method)) {
    return CARD_ADMIN_CHANNELS[method].fiscal;
  }

  return method.replace(/\D/g, "") as PAYMENT_METHODS;
}

export const PaymentMethodsKeys = Object.keys(PAYMENT_METHODS) as PAYMENT_METHOD[];
